import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { PublicNav, PublicFooter } from "../components/PublicLayout";
import {
  ArrowRight, Phone, ChevronDown, Star,
  CheckCircle2, Zap, PhoneOff, DollarSign, Users, Clock,
  BarChart3, ShieldCheck, MessageSquare
} from "lucide-react";

const TESTIMONIALS = [
  { quote: "Since adding Zest, we capture 75+ extra orders every month. For a multi-unit operator, that's a no-brainer.", name: "Michael H.", role: "Multi-unit Pizza Operator", revenue: "$8,500+/mo extra revenue" },
  { quote: "This was the first Halloween I actually left early. Zest was answering every call perfectly.", name: "Costa A.", role: "Restaurant Owner", revenue: "Zero missed calls on busiest night" },
  { quote: "It paid for itself in 10 days. Phones are calm, tickets are bigger, and my team refuses to go back.", name: "Nick H.", role: "Pizza Restaurant Owner", revenue: "ROI in under 2 weeks" },
  { quote: "If I didn't have Zest answering phones, I'd be losing out on over $200K a year.", name: "Dru M.", role: "Restaurant Owner", revenue: "$200K+ annual impact" },
];

const FAQS = [
  { q: "Is Voice AI cheaper and better than a human or answering service?", a: "Yes. Zest answers 100% of calls, never calls in sick, and follows your menu and policies perfectly. Most operators cut phone staffing costs, reduce order errors, and capture orders they used to miss. You pay a predictable monthly fee instead of hourly wages." },
  { q: "What can Zest handle on a call?", a: "Pickup and delivery orders, menu and allergen questions, hours and directions, reservations, wait times, catering inquiries, and smart routing to your team. It handles multiple simultaneous calls and supports complex orders with modifiers." },
  { q: "How fast is setup?", a: "Zest can get you live in under an hour. Connect your POS, import your menu (or upload a PDF), set your hours and greeting, and go live. Our team can do white-glove setup if you prefer." },
  { q: "Does it work for multi-location groups?", a: "Yes. Manage shared menus with per-store overrides, local numbers, store-specific hours, and location-based routing. Analytics roll up by location so you see performance per store." },
  { q: "What happens with edge cases or unhappy callers?", a: "Zest detects intent and either solves it or transfers cleanly to your staff with full context. No caller gets stuck in a loop." },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <PublicNav />

      {/* ─── Hero ─── */}
      <section className="pt-28 pb-16 sm:pt-36 sm:pb-24 px-5">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-100 mb-6">
            <span className="text-[12px] font-semibold text-orange-700">Join 1,000+ restaurants using Zest</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.08] max-w-4xl mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
            24/7 AI Phone Answering<br />for Restaurants
          </h1>
          <p className="text-base sm:text-lg text-slate-500 mt-6 max-w-2xl mx-auto leading-relaxed">
            Zest answers every call, takes pickup and delivery orders, handles customer questions, and sends orders straight to your POS. Never miss a call again.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Button size="lg" className="h-12 px-8 text-[15px] font-semibold bg-orange-500 hover:bg-orange-600 shadow-lg shadow-orange-500/25" onClick={() => navigate("/register")} data-testid="hero-cta-button">
              Get started <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button variant="outline" size="lg" className="h-12 px-8 text-[15px] font-semibold" onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-demo-button">
              See how it works
            </Button>
          </div>
        </div>
      </section>

      {/* ─── Pain Points ─── */}
      <section className="py-16 px-5 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-center mb-12" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Every missed call = Lost revenue
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: PhoneOff, title: "30% of calls missed during peak hours", desc: "Your staff is slammed. The phone rings. Nobody picks up. That customer orders from your competitor instead." },
              { icon: DollarSign, title: "Every call is a potential $25–$50 ticket", desc: "Multiply that by 10 missed calls a day. That's $250–$500 walking out the door. Every. Single. Day." },
              { icon: Users, title: "Staff pulled off the floor to answer phones", desc: "When your cook or cashier stops to answer the phone, everyone waiting in line suffers. Service slows down." },
            ].map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="text-center">
                  <div className="w-14 h-14 rounded-2xl bg-orange-500/10 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-orange-400" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-base font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{p.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Features ─── */}
      <section id="features" className="py-20 sm:py-28 px-5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Your all-in-one AI phone agent
            </h2>
            <p className="text-base text-slate-500 mt-4 max-w-2xl mx-auto">
              Zest handles everything your staff does on the phone — but faster, more accurately, and 24/7.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Phone, title: "Takes orders by phone", desc: "Handles pickup and delivery orders with full modifier support. Reads back every order for confirmation." },
              { icon: MessageSquare, title: "Answers customer questions", desc: "Uses your restaurant's knowledge graph to answer menu, allergen, hours, and direction questions accurately." },
              { icon: ShieldCheck, title: "Sends orders to your POS", desc: "Clean tickets land directly in your POS system. No re-keying, no paper tickets, no mistakes." },
              { icon: BarChart3, title: "Real-time dashboard", desc: "See every call, order, and conversion. AI insights surface what's selling and when you're busiest." },
              { icon: Zap, title: "Handles unlimited calls", desc: "Never puts a customer on hold. Handles multiple simultaneous calls during your busiest rushes." },
              { icon: Clock, title: "Live in under an hour", desc: "Upload your menu or sync from POS. Set your hours and greeting. Start answering calls today." },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="p-6 rounded-2xl border border-slate-100 hover:border-orange-200 hover:shadow-md transition-all duration-200" data-testid={`feature-card-${i}`}>
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-orange-600" strokeWidth={1.8} />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>{f.title}</h3>
                  <p className="text-[13px] text-slate-500 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Testimonials ─── */}
      <section className="py-20 px-5 bg-slate-50/80">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 text-center mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Why restaurant operators love Zest
          </h2>
          <p className="text-base text-slate-500 text-center mb-12 max-w-xl mx-auto">Real results from real restaurants.</p>
          <div className="grid sm:grid-cols-2 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`testimonial-${i}`}>
                <div className="flex gap-0.5 mb-3">
                  {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 text-orange-400 fill-orange-400" />)}
                </div>
                <p className="text-[14px] text-slate-700 leading-relaxed mb-4">"{t.quote}"</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{t.name}</p>
                    <p className="text-[11px] text-slate-400">{t.role}</p>
                  </div>
                  <span className="text-[11px] font-semibold text-green-600 bg-green-50 px-2.5 py-1 rounded-full">{t.revenue}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How it Works ─── */}
      <section id="how-it-works" className="py-20 sm:py-28 px-5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Live in under an hour. Seriously.
            </h2>
            <p className="text-base text-slate-500 mt-4 max-w-xl mx-auto">No engineers. No training. No six-month rollout.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { num: "01", title: "Connect your POS", desc: "Link your POS system in minutes. We pull your menu automatically and build your restaurant's knowledge graph." },
              { num: "02", title: "Get your AI number", desc: "We provision a local phone number for your restaurant. Forward your existing line or use it directly." },
              { num: "03", title: "Start taking orders", desc: "Your AI agent starts answering calls immediately. Monitor everything from your real-time dashboard." },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <span className="text-6xl font-bold text-orange-100" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.num}</span>
                <h3 className="text-lg font-bold text-slate-900 mt-1 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.title}</h3>
                <p className="text-[14px] text-slate-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Pricing ─── */}
      <section id="pricing" className="py-20 sm:py-28 px-5 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Choose a plan</h2>
            <p className="text-base text-slate-400 mt-4 max-w-xl mx-auto">Transparent pricing that pays for itself. Most restaurants see ROI within the first week.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Starter */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-7">
              <p className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-1">Starter</p>
              <p className="text-[13px] text-slate-500 mb-4">For basic call management</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>$199</span>
                <span className="text-slate-400 text-sm">/mo</span>
              </div>
              <p className="text-[12px] text-slate-500 mb-6">plus $149 setup fee</p>
              <ul className="space-y-2.5 mb-7">
                {["Humanlike voice AI", "Handle customer inquiries", "Restaurant knowledge graph", "Automated call transfer", "Automatic menu updates", "Handle up to 50 simultaneous calls", "Interruption handling", "Outbound SMS", "Customer dashboard"].map(f => (
                  <li key={f} className="flex items-center gap-2 text-[13px] text-slate-300"><CheckCircle2 className="w-3.5 h-3.5 text-orange-400 shrink-0" />{f}</li>
                ))}
              </ul>
              <Button className="w-full h-11 text-sm font-semibold bg-white text-slate-900 hover:bg-slate-100" onClick={() => navigate("/register")} data-testid="starter-cta">
                Get started <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
            {/* Premium */}
            <div className="bg-orange-500 rounded-2xl p-7 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white text-orange-600 text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow-sm">Most Popular</div>
              <p className="text-sm font-semibold text-orange-100 uppercase tracking-wider mb-1">Premium</p>
              <p className="text-[13px] text-orange-200 mb-4">For ordering & reservations</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>$399</span>
                <span className="text-orange-200 text-sm">/mo</span>
              </div>
              <p className="text-[12px] text-orange-200 mb-6">plus $149 setup fee</p>
              <ul className="space-y-2.5 mb-7">
                {["Everything in Starter", "Takeout and delivery ordering", "Complete POS integration", "Automated phone reservations", "Secure payment over the phone", "Smart guest memory", "Dynamic upsell engine", "Out-of-stock AI menu updates", "Intelligent SMS updates"].map(f => (
                  <li key={f} className="flex items-center gap-2 text-[13px] text-white"><CheckCircle2 className="w-3.5 h-3.5 text-white shrink-0" />{f}</li>
                ))}
              </ul>
              <Button className="w-full h-11 text-sm font-semibold bg-white text-orange-600 hover:bg-orange-50" onClick={() => navigate("/register")} data-testid="premium-cta">
                Get started <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
          <p className="text-center text-[13px] text-slate-500 mt-6">
            Looking for custom integrations or enterprise features?{" "}
            <a href="mailto:hello@getzest.ai" className="text-orange-400 hover:underline">Contact us</a> for enterprise plans.
          </p>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section id="faq" className="py-20 sm:py-28 px-5">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 text-center mb-12" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Frequently asked questions
          </h2>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className="border border-slate-100 rounded-xl overflow-hidden" data-testid={`faq-${i}`}>
                <button
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50/50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="text-[14px] font-semibold text-slate-800 pr-4">{faq.q}</span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 -mt-1">
                    <p className="text-[13px] text-slate-500 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="py-20 sm:py-28 px-5 bg-orange-500 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Turn every call into cash.
          </h2>
          <p className="text-base text-orange-100 max-w-xl mx-auto mb-8">
            Your phone is ringing. Is anyone answering? Zest captures every call so you never lose another order.
          </p>
          <Button size="lg" className="h-12 px-8 text-[15px] font-semibold bg-white text-orange-600 hover:bg-orange-50 shadow-lg" onClick={() => navigate("/register")} data-testid="final-cta-button">
            Get started now <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
